import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Box, Button, CircularProgress, IconButton, Paper, Slider, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { storeVerifiedEntitlement } from '../utils/purchaseEntitlement';

const AudioPlayerContext = createContext(null);
export const useAudioPlayer = () => useContext(AudioPlayerContext);
const formatTime = seconds => {
    const value = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
};

// This component stays mounted around the routes, so navigation never replaces the audio element.
export default function AudioPlayer({ children }) {
    const audioRef = useRef(null);
    const panelRef = useRef(null);
    const trackRef = useRef(null);
    const operation = useRef(0);
    const wantsPlay = useRef(false);
    const visibleRef = useRef(false);
    const refreshed = useRef(false);
    const resumeAt = useRef(null);
    const [track, setTrack] = useState(null);
    const [visible, setVisible] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [loading, setLoading] = useState(false);
    const [position, setPosition] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [height, setHeight] = useState(0);
    const [error, setError] = useState('');
    const [downloadError, setDownloadError] = useState('');
    const [downloading, setDownloading] = useState(false);

    const pause = useCallback(() => {
        operation.current += 1; // Ignore a URL refresh or play request that finishes after pausing.
        wantsPlay.current = false;
        audioRef.current.pause();
        setPlaying(false);
        setLoading(false);
    }, []);

    const hidePlayer = useCallback(() => {
        pause();
        visibleRef.current = false;
        setVisible(false);
    }, [pause]);

    const openPlayer = useCallback((nextTrack, { restore = false } = {}) => {
        // Filters and route changes must not reopen a player the listener deliberately hid.
        if (restore && trackRef.current) return;
        const audio = audioRef.current;
        if (trackRef.current?.sessionId !== nextTrack.sessionId || trackRef.current?.title !== nextTrack.title) {
            pause();
            trackRef.current = nextTrack;
            setTrack(nextTrack);
            setPosition(0);
            setDuration(0);
            setError('');
            setDownloadError('');
            refreshed.current = false;
            resumeAt.current = null;
            audio.src = nextTrack.url;
            audio.load();
        }
        // Reopening the same purchase keeps its position and any refreshed S3 URL.
        visibleRef.current = true;
        setVisible(true);
    }, [pause]);

    const controls = useMemo(() => ({ openPlayer, hidePlayer }), [openPlayer, hidePlayer]);

    const playAudio = async request => {
        wantsPlay.current = true;
        setLoading(true);
        setError('');
        try {
            await audioRef.current.play();
        } catch (failure) {
            if (request !== operation.current) return;
            wantsPlay.current = false;
            setPlaying(false);
            setLoading(false);
            setError(failure.name === 'NotAllowedError'
                ? '' // Keep Play available in the next user gesture, without another network request.
                : 'The audio could not play. Press Retry to try again.');
        }
    };

    const refreshAudio = async autoplay => {
        const request = ++operation.current;
        const selected = trackRef.current;
        const audio = audioRef.current;
        const positionToRestore = resumeAt.current ?? audio.currentTime;
        setLoading(true);
        setError('');
        try {
            if (!selected?.sessionId) throw new Error('Missing purchase');
            const { data } = await axios.post('/api/verify-payment', { sessionId: selected.sessionId });
            if (request !== operation.current) return;
            if (!data?.verified || !data.urlMP3) throw new Error('Purchase not verified');
            storeVerifiedEntitlement({ audioUrl: data.urlMP3, sessionId: selected.sessionId });
            resumeAt.current = positionToRestore;
            audio.src = data.urlMP3;
            audio.load();
            if (autoplay) await playAudio(request);
            else setLoading(false);
        } catch {
            if (request !== operation.current) return;
            wantsPlay.current = false;
            setLoading(false);
            setError('Could not reload your song. Try again, or reopen the access link in your purchase email.');
        }
    };

    const play = () => {
        refreshed.current = false;
        if (error || audioRef.current.error) {
            refreshed.current = true;
            refreshAudio(true);
        } else {
            playAudio(++operation.current);
        }
    };

    const download = async () => {
        setDownloading(true);
        setDownloadError('');
        try {
            const { data } = await axios.post('/api/verify-payment', {
                sessionId: trackRef.current.sessionId, intent: 'download',
            });
            if (!data?.verified || !data.urlMP3) throw new Error('Download unavailable');
            // S3's signed attachment response makes this download across origins without fetching a blob.
            const link = document.createElement('a');
            link.href = data.urlMP3;
            link.download = '';
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch {
            setDownloadError('The download could not start. Please try Download again.');
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => {
        if (!visible) return;
        const updateHeight = () => setHeight(panelRef.current.offsetHeight);
        updateHeight();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateHeight);
        observer?.observe(panelRef.current);
        window.addEventListener('resize', updateHeight);
        return () => { observer?.disconnect(); window.removeEventListener('resize', updateHeight); };
    }, [visible]);

    useEffect(() => {
        const audio = audioRef.current;
        return () => { operation.current += 1; audio.pause(); };
    }, []);

    return (
        <AudioPlayerContext.Provider value={controls}>
            <Box sx={{ pb: visible ? `${height}px` : 0 }}>{children}</Box>
            <audio ref={audioRef} preload="metadata"
                onLoadedMetadata={() => {
                    const audio = audioRef.current;
                    setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
                    if (resumeAt.current !== null && Number.isFinite(audio.duration)) {
                        audio.currentTime = resumeAt.current < audio.duration ? resumeAt.current : 0;
                        resumeAt.current = null;
                    }
                    setPosition(audio.currentTime);
                }}
                onTimeUpdate={() => setPosition(audioRef.current.currentTime)}
                onPlaying={() => {
                    if (!wantsPlay.current) { audioRef.current.pause(); return; }
                    setPlaying(true); setLoading(false); setError('');
                }}
                onPause={() => setPlaying(false)}
                onWaiting={() => { if (wantsPlay.current) setLoading(true); }}
                onEnded={() => { wantsPlay.current = false; setPlaying(false); setLoading(false); }}
                onError={() => {
                    if (!visibleRef.current || !audioRef.current.getAttribute('src')) return;
                    setPlaying(false);
                    if (!refreshed.current) {
                        refreshed.current = true;
                        refreshAudio(wantsPlay.current);
                    } else {
                        wantsPlay.current = false;
                        setLoading(false);
                        setError('The audio could not load. Check your connection and press Retry.');
                    }
                }}
            />
            <Paper ref={panelRef} component="section" aria-label="Exclusive audio player" elevation={12} square
                sx={{ display: visible ? 'block' : 'none', position: 'fixed', bottom: 0, left: 0, right: 0,
                    zIndex: theme => theme.zIndex.appBar, borderTop: 1, borderColor: 'divider', pb: 'env(safe-area-inset-bottom)' }}>
                <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary">PURCHASED EXCLUSIVE</Typography>
                            <Typography noWrap fontWeight={700}>{track?.title} · Eli</Typography>
                        </Box>
                        <Button startIcon={<DownloadIcon />} onClick={download} disabled={downloading}>
                            {downloading ? 'Downloading...' : 'Download'}
                        </Button>
                        <IconButton aria-label="Close and pause player" onClick={hidePlayer}><CloseIcon /></IconButton>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <IconButton aria-label={playing || loading ? 'Pause audio' : error ? 'Retry audio' : 'Play audio'}
                            onClick={playing || loading ? pause : play} sx={{ width: 44, height: 44 }}>
                            {loading ? <CircularProgress size={24} /> : playing ? <PauseIcon /> : <PlayArrowIcon />}
                        </IconButton>
                        <Typography variant="caption">{formatTime(position)}</Typography>
                        <Slider aria-label="Playback position" min={0} max={duration || 1} step={0.1}
                            value={Math.min(position, duration || 0)} disabled={!duration}
                            getAriaValueText={value => `${formatTime(value)} of ${formatTime(duration)}`}
                            onChange={(_, value) => { audioRef.current.currentTime = value; setPosition(value); }} sx={{ flex: 1 }} />
                        <Typography variant="caption">{formatTime(duration)}</Typography>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ display: { xs: 'none', md: 'flex' }, width: 130 }}>
                            <IconButton aria-label={volume ? 'Mute audio' : 'Unmute audio'}
                                onClick={() => { const value = volume ? 0 : 1; audioRef.current.volume = value; setVolume(value); }}>
                                {volume ? <VolumeUpIcon /> : <VolumeOffIcon />}
                            </IconButton>
                            <Slider aria-label="Volume" min={0} max={1} step={0.01} value={volume}
                                onChange={(_, value) => { audioRef.current.volume = value; setVolume(value); }} />
                        </Stack>
                    </Stack>
                    {error && <Alert severity="error" action={<Button color="inherit" onClick={play}>Retry</Button>}>{error}</Alert>}
                    {downloadError && <Alert severity="error">{downloadError}</Alert>}
                </Box>
            </Paper>
        </AudioPlayerContext.Provider>
    );
}
